import { getTheme, IStackItemStyles, Stack, StackItem } from '@fluentui/react';
import { useParams } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { QueueConfiguration, Title } from 'services';
import { DetailsForm } from './DetailsForm';
import { ModerationForm } from './ModerationForm';
import { TraitsForm } from './TraitsForm';
import { CreateQueueTitle } from './CreateQueueTitle';
import { FlowFormTitle, FlowState } from './FlowFormTitle';

interface CreateQueueFlowProps {
  configs: QueueConfiguration[];
}

interface CreateQueueFlowRouteParams {
  tenantId: string;
}

const enum TabKey {
  Details = 1,
  Traits = 2,
  Moderation = 3
}

export const CreateQueueFlow: React.FC<CreateQueueFlowProps> = props => {
  const { palette } = getTheme();

  const routeParams: CreateQueueFlowRouteParams = useParams();

  const [currentTab, setCurrentTab] = useState(TabKey.Details);

  const [newQueue, setNewQueue] = useState<QueueConfiguration>({
    defaultMaxAssignedCaseCount: 15,
    caseOpenLifetimeInSeconds: 60,
    caseAssignedLifetimeInSeconds: 60,
    caseOpenLifetimeWarningFactor: 0.9,
    revision: 0,
    sourceTypeWeightMappings: { AutoModXuid: 4, Default: 0, Moderator: 3, Xuid: 1 },
    minimumModeratorFeedbackAgreementCount: 1,
    caseOpenLifetimeMaxInSeconds: 604800,
    uxPreferences: { subjectImage: { blurStrengthPixels: 10 } },
    useUsersFromAadGroup: true,
    tenant: { tenantId: routeParams.tenantId }
  } as QueueConfiguration);

  // Grabs the first queue in this tenant, which let's us copy some info that would otherwise be hard to get including:
  // Tenant display name
  // allowed admins
  // allowed moderators
  // categorized traits
  // TODO Deliverable: https://microsoft.visualstudio.com/Xbox/_workitems/edit/37800930
  // TODO: Add in the appropriate APIS to get this info without this awkward copy
  const firstMatchingQueue = (): QueueConfiguration => {
    // we can set this forcibly because we know the props will always contain a matching tenantId
    let returnval = {} as QueueConfiguration;
    for (let index = 0; index < props.configs.length; index++) {
      const queueConfig = props.configs[index];
      if (queueConfig.tenant.tenantId === routeParams.tenantId) {
        returnval = queueConfig;
        break;
      }
    }
    return returnval;
  };

  const [matchingQueue, setMatchingQueue] = useState<QueueConfiguration>();

  useEffect(() => {
    setMatchingQueue(firstMatchingQueue());
  }, [props.configs]);

  useEffect(() => {
    if (matchingQueue?.displayName) {
      setNewQueue({
        ...newQueue,
        tenant: { ...newQueue.tenant, displayName: matchingQueue.tenant.displayName },
        allowedAdmins: [],
        allowedModerators: [],
        categorizedTraits: matchingQueue.categorizedTraits
      });
    }
  }, [matchingQueue]);

  const [currentFormComplete, setCurrentFormComplete] = useState<boolean>(false);

  const lineStyle: Partial<IStackItemStyles> = {
    root: {
      selectors: {
        '::before': {
          backgroundColor: palette.neutralLight,
          bottom: 0,
          left: 0,
          height: 2,
          width: '100%',
          content: `''`
        }
      }
    }
  };

  const onFormChange = (updatedForm: QueueConfiguration) => {
    setNewQueue(updatedForm);
  };

  const getValidTitles = (): Title[] => {
    const validTitles: Title[] = [];
    props.configs.forEach(queue => {
      if (
        queue.tenant.tenantId === routeParams.tenantId &&
        !validTitles.map(title => title.titleId).includes(queue.title.titleId)
      ) {
        validTitles.push(queue.title);
      }
    });
    return validTitles;
  };

  const renderTab = () => {
    switch (currentTab) {
      case TabKey.Details:
        return (
          <DetailsForm
            onFormComplete={setCurrentFormComplete}
            onFormChange={onFormChange}
            currentQueue={newQueue}
            validTitles={getValidTitles()}
          />
        );
      case TabKey.Traits:
        return (
          <TraitsForm onFormComplete={setCurrentFormComplete} onFormChange={onFormChange} currentQueue={newQueue} />
        );
      case TabKey.Moderation:
        return (
          <ModerationForm onFormComplete={setCurrentFormComplete} onFormChange={onFormChange} currentQueue={newQueue} />
        );
    }
  };

  const goToNextTab = () => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    if (currentTab < 3) {
      setCurrentTab(currentTab + 1);
    }
  };

  const goToPreviousTab = () => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    if (currentTab > 1) {
      setCurrentTab(currentTab - 1);
    }
  };

  const calculateFlowState = (flowStateNumber: number) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    if (currentTab === flowStateNumber && !currentFormComplete) {
      return FlowState.Active;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    } else if (currentTab < flowStateNumber) {
      return FlowState.InActive;
    } else {
      return FlowState.Complete;
    }
  };

  return (
    <Stack className="h-100" style={{ backgroundColor: palette.neutralLighterAlt }}>
      <Stack.Item>
        <Stack className="h-100 position-relative">
          <CreateQueueTitle
            newQueue={newQueue}
            tenantDisplayName={newQueue?.tenant?.displayName}
            tenantId={routeParams.tenantId}
            goToNextTab={goToNextTab}
            goToPreviousTab={goToPreviousTab}
            currentTab={currentTab}
            currentFormComplete={currentFormComplete}
          />
        </Stack>
      </Stack.Item>
      <Stack styles={lineStyle}></Stack>
      <Stack horizontal style={{ alignItems: 'center', justifyContent: 'center', backgroundColor: palette.white }}>
        <FlowFormTitle title={'Queue Details'} flowState={calculateFlowState(1)} flowStateNumber={1} />
        <Stack styles={lineStyle} style={{ width: 80 }}></Stack>
        <FlowFormTitle title={'Traits'} flowState={calculateFlowState(2)} flowStateNumber={2} />
        <Stack styles={lineStyle} style={{ width: 80 }}></Stack>
        <FlowFormTitle title={'Moderation'} flowState={calculateFlowState(3)} flowStateNumber={3} />
      </Stack>
      <Stack styles={lineStyle}></Stack>
      <StackItem grow className="px-5 pt-5 overflow-auto" style={{ backgroundColor: palette.white }}>
        <Stack>{renderTab()}</Stack>
      </StackItem>
    </Stack>
  );
};
